import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  loadConsent,
  saveConsent,
  getPrefs,
  applyConsent,
} from "../consent/consentManager";

const COPY = {
  it: {
    bannerTitle: "Rispettiamo la tua privacy",
    bannerText:
      "Questo sito usa cookie e strumenti tecnici necessari al suo funzionamento. Previo tuo consenso, utilizza anche strumenti di analisi (PostHog) e servizi di terze parti (mappa Google, script della piattaforma di hosting Emergent). Puoi accettare, rifiutare o personalizzare la scelta. Chiudere con la X equivale a proseguire senza acconsentire ai cookie non tecnici.",
    accept: "Accetta tutto",
    reject: "Rifiuta tutto",
    customize: "Personalizza",
    close: "Chiudi senza acconsentire",
    policyLinks: "Privacy Policy · Cookie Policy",
    privHref: "/privacy-policy/",
    cookHref: "/cookie-policy/",

    prefTitle: "Preferenze cookie",
    prefSub:
      "Attiva o disattiva le categorie di cookie non tecnici. I cookie necessari sono sempre attivi perché indispensabili al funzionamento del sito.",
    catNecessary: "Necessari",
    catNecessaryDesc:
      "Indispensabili al funzionamento del sito (ad es. memorizzazione della tua scelta sui cookie). Sempre attivi, non richiedono consenso.",
    catAnalytics: "Analytics",
    catAnalyticsDesc:
      "PostHog Analytics (server USA) raccoglie dati di navigazione aggregati e registrazioni delle sessioni per aiutarci a migliorare il sito. Vedi la Cookie Policy.",
    catThirdparty: "Servizi di terze parti",
    catThirdpartyDesc:
      "Google Maps embed (mostra la mappa interattiva nella sezione Indirizzo) e script della piattaforma Emergent che ospita il sito. Senza consenso, al posto della mappa interattiva vedrai un segnaposto cliccabile.",
    save: "Salva preferenze",
    acceptAll: "Accetta tutto",
    rejectAll: "Rifiuta tutto",
  },
  en: {
    bannerTitle: "We respect your privacy",
    bannerText:
      "This site uses strictly necessary technical cookies. With your consent, it also uses analytics tools (PostHog) and third-party services (Google Maps embed, hosting platform script by Emergent). You can accept, reject or customise your choice. Closing with the X is equivalent to continuing without consent to non-technical cookies.",
    accept: "Accept all",
    reject: "Reject all",
    customize: "Customise",
    close: "Close without consenting",
    policyLinks: "Privacy Policy · Cookie Policy",
    privHref: "/en/privacy-policy/",
    cookHref: "/en/cookie-policy/",

    prefTitle: "Cookie preferences",
    prefSub:
      "Enable or disable non-technical cookie categories. Necessary cookies are always on because they are indispensable for the site to work.",
    catNecessary: "Necessary",
    catNecessaryDesc:
      "Required for the site to function (e.g. storing your cookie choice). Always on, no consent required.",
    catAnalytics: "Analytics",
    catAnalyticsDesc:
      "PostHog Analytics (US servers) collects aggregated browsing data and session recordings to help us improve the site. See the Cookie Policy.",
    catThirdparty: "Third-party services",
    catThirdpartyDesc:
      "Google Maps embed (shows the interactive map in the Address section) and the Emergent hosting-platform script. Without consent, a clickable placeholder replaces the interactive map.",
    save: "Save preferences",
    acceptAll: "Accept all",
    rejectAll: "Reject all",
  },
};

const ConsentBanner = ({ lang }) => {
  const t = COPY[lang] || COPY.it;
  const [state, setState] = useState("hidden"); // 'hidden' | 'banner' | 'preferences'
  const [sel, setSel] = useState({ analytics: false, thirdparty: false });

  // On mount: if no stored consent, show banner. Also apply stored prefs.
  useEffect(() => {
    const stored = loadConsent();
    if (stored) {
      applyConsent(stored.prefs);
      setSel({
        analytics: !!stored.prefs.analytics,
        thirdparty: !!stored.prefs.thirdparty,
      });
    } else {
      setState("banner");
    }
    const openPref = () => {
      const prefs = getPrefs();
      if (prefs) {
        setSel({ analytics: !!prefs.analytics, thirdparty: !!prefs.thirdparty });
      }
      setState("preferences");
    };
    window.addEventListener("ptr-open-preferences", openPref);
    window.ptrConsent = {
      showPreferences: openPref,
      getPrefs,
    };
    return () => window.removeEventListener("ptr-open-preferences", openPref);
  }, []);

  const commit = (p) => {
    saveConsent(p);
    setState("hidden");
  };

  if (state === "hidden") return null;

  if (state === "preferences") {
    return (
      <div
        data-testid="consent-preferences"
        className="fixed inset-0 z-[200] flex items-end md:items-center justify-center bg-black/70 backdrop-blur-sm p-0 md:p-6"
        role="dialog"
        aria-modal="true"
      >
        <div className="w-full md:max-w-[640px] bg-[#111] border border-[#800020]/40 rounded-t-2xl md:rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col">
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
            <h3 className="font-caveat text-2xl md:text-3xl text-[#f4f4f0]">
              {t.prefTitle}
            </h3>
            <button
              data-testid="consent-close-prefs"
              onClick={() => {
                // closing preferences without saving = treat as reject non-essentials
                if (!loadConsent()) commit({ analytics: false, thirdparty: false });
                else setState("hidden");
              }}
              aria-label={t.close}
              className="w-10 h-10 grid place-items-center rounded-full border border-white/15 text-white/80 hover:bg-[#800020] hover:border-[#800020] transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          <div className="px-5 py-4 overflow-y-auto flex-1">
            <p className="font-outfit text-[13px] text-[#a1a1aa] leading-relaxed mb-5">
              {t.prefSub}
            </p>

            {/* Necessary - always on, disabled */}
            <PrefRow
              name={t.catNecessary}
              desc={t.catNecessaryDesc}
              checked
              disabled
            />

            <PrefRow
              name={t.catAnalytics}
              desc={t.catAnalyticsDesc}
              checked={sel.analytics}
              onChange={(v) => setSel((s) => ({ ...s, analytics: v }))}
              testid="pref-analytics"
            />

            <PrefRow
              name={t.catThirdparty}
              desc={t.catThirdpartyDesc}
              checked={sel.thirdparty}
              onChange={(v) => setSel((s) => ({ ...s, thirdparty: v }))}
              testid="pref-thirdparty"
            />
          </div>

          <div className="px-5 py-4 border-t border-white/10 flex flex-col sm:flex-row gap-2 sm:items-center sm:justify-between">
            <div className="flex gap-2 flex-wrap">
              <button
                data-testid="pref-reject-all"
                onClick={() => commit({ analytics: false, thirdparty: false })}
                className="px-4 py-2.5 border border-white/20 text-white/85 hover:text-white hover:border-white/40 font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors"
              >
                {t.rejectAll}
              </button>
              <button
                data-testid="pref-accept-all"
                onClick={() => commit({ analytics: true, thirdparty: true })}
                className="px-4 py-2.5 border border-white/20 text-white/85 hover:text-white hover:border-white/40 font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors"
              >
                {t.acceptAll}
              </button>
            </div>
            <button
              data-testid="pref-save"
              onClick={() => commit(sel)}
              className="px-5 py-2.5 bg-[#800020] hover:bg-[#9e0028] text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full transition-colors"
            >
              {t.save}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Banner
  return (
    <div
      data-testid="consent-banner"
      role="dialog"
      aria-modal="false"
      aria-label={t.bannerTitle}
      className="fixed inset-x-0 bottom-0 z-[150] p-3 md:p-5"
    >
      <div className="max-w-[1200px] mx-auto bg-[#111] border border-[#800020]/40 rounded-xl shadow-2xl p-5 md:p-6">
        <div className="flex items-start gap-3 mb-3">
          <h3 className="font-caveat text-2xl md:text-3xl text-[#f4f4f0] flex-1">
            {t.bannerTitle}
          </h3>
          <button
            data-testid="consent-close"
            onClick={() => commit({ analytics: false, thirdparty: false })}
            aria-label={t.close}
            className="w-9 h-9 grid place-items-center rounded-full border border-white/15 text-white/80 hover:bg-[#800020] hover:border-[#800020] transition-colors shrink-0"
          >
            <X size={16} />
          </button>
        </div>

        <p className="font-outfit text-[13px] md:text-sm text-[#d1d1cf] leading-relaxed mb-4">
          {t.bannerText}
        </p>
        <p className="font-outfit text-[11px] text-[#a1a1aa] mb-4">
          <a href={t.privHref} className="underline hover:text-white">
            Privacy Policy
          </a>
          {" · "}
          <a href={t.cookHref} className="underline hover:text-white">
            Cookie Policy
          </a>
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          <button
            data-testid="consent-reject"
            onClick={() => commit({ analytics: false, thirdparty: false })}
            className="px-4 py-3 border border-white/25 text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full hover:border-white/60 hover:bg-white/5 transition-colors"
          >
            {t.reject}
          </button>
          <button
            data-testid="consent-customize"
            onClick={() => setState("preferences")}
            className="px-4 py-3 border border-white/25 text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full hover:border-white/60 hover:bg-white/5 transition-colors"
          >
            {t.customize}
          </button>
          <button
            data-testid="consent-accept"
            onClick={() => commit({ analytics: true, thirdparty: true })}
            className="px-4 py-3 border border-white/25 text-white font-outfit text-[11px] uppercase tracking-[0.22em] rounded-full hover:border-white/60 hover:bg-white/5 transition-colors"
          >
            {t.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

const PrefRow = ({ name, desc, checked, disabled, onChange, testid }) => (
  <div className="flex items-start gap-4 py-4 border-t border-white/10 first:border-t-0">
    <div className="flex-1">
      <p className="font-outfit text-sm font-semibold text-white">{name}</p>
      <p className="font-outfit text-[12px] text-[#a1a1aa] mt-1 leading-relaxed">
        {desc}
      </p>
    </div>
    <label className="relative inline-flex items-center shrink-0 cursor-pointer">
      <input
        type="checkbox"
        data-testid={testid}
        className="sr-only"
        checked={!!checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
      />
      <span
        className={`w-11 h-6 rounded-full transition-colors ${
          checked ? "bg-[#5BBF7A]" : "bg-white/15"
        } ${disabled ? "opacity-50" : ""}`}
      />
      <span
        className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform ${
          checked ? "translate-x-5" : ""
        }`}
      />
    </label>
  </div>
);

export default ConsentBanner;
